<template>
  <div class="dashboard-page" v-if="checkedAuth">
    <MetaManager
      title="Connect Shopify Store"
      description="Connect your Shopify store to sync gift cards"
    />
    <div class="dashboard-content">
      <h1>Connect Shopify Store</h1>
      
      <div class="connect-container">        
        <!-- Prompt for sync if redirected from /install -->
        <div v-if="showPromptSync" class="prompt-sync-message">
          <span class="prompt-icon">🔗</span>
          <strong>To finish connecting your Shopify store, click the button below to sync your platform.</strong>
          <p class="prompt-details">This step is required to authorize the app and enable gift card syncing. You will be redirected to Shopify to complete the connection.</p>
        </div>
        <!-- Success message -->
        <div v-if="successMessage" class="success-message">
          {{ successMessage }}
        </div>
        
        <!-- Show disconnect UI if connected -->
        <div v-if="currentStatus && currentStatus.isConnected" class="current-status">
          <h3>Current Connection Status</h3>
          <div class="status-connected">
            <span class="status-icon">✅</span>
            <span>Connected to: {{ currentStatus.shopDomain }}</span>
            <button @click="disconnectStore" class="disconnect-btn">
              Disconnect
            </button>
          </div>
        </div>
        
        <!-- Only show connect form if NOT connected -->
        <div v-else class="connect-form">
          <h3>Connect Your Store</h3>
          <div class="connect-info">
            <p>Connect your Shopify store to automatically sync gift cards and track redemptions.</p>
            <ul>
              <li>✅ Automatic gift card creation</li>
              <li>✅ Real-time redemption tracking</li>
              <li>✅ Secure OAuth authentication</li>
              <li>✅ No manual setup required</li>
            </ul>
          </div>
          
          <div class="connect-info">
            <p>Install the Gift Card Sync app on your Shopify store to automatically sync gift cards and track redemptions.</p>
          </div>
          
          <div class="install-actions">
            <button 
              @click="installApp" 
              :disabled="isConnecting"
              class="connect-btn primary"
            >
              <span v-if="isConnecting">Installing...</span>
              <span v-else>Sync Shopify App</span>
            </button>
          </div>
          
          <div v-if="error" class="error-message">
            {{ error }}
          </div>
          
          <!-- OAuth Error Display -->
          <div v-if="oauthError" class="oauth-error-message">
            <div class="oauth-error-header">
              <span class="error-icon">⚠️</span>
              <h4>Connection Failed</h4>
            </div>
            <p>{{ oauthError.message }}</p>
            <div class="oauth-error-actions">
              <button @click="retryOAuth" class="retry-btn" :disabled="isConnecting">
                <span v-if="isConnecting">Retrying...</span>
                <span v-else>Retry Connection</span>
              </button>
              <button @click="oauthError = null" class="dismiss-btn">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MetaManager from '../../components/MetaManager.vue';

export default {
  name: 'DashboardShopifyConnect',
  components: {
    MetaManager,
  },
  data() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      isConnecting: false,
      error: '',
      token: localStorage.getItem('token'),
      currentStatus: null,
      oauthError: null,
      successMessage: '',
      checkedAuth: false, // Only true after auth check passes
      shopParamPresent: !!urlParams.get('shop'),
      showPromptSync: urlParams.get('shopify') === 'prompt_sync',
    };
  },

  methods: {

    
    async checkCurrentStatus() {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const businessId = user?.business_id;
        if (!businessId) return;

        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/shopify/status/${businessId}` ,{
          headers: { 'Authorization': `Bearer ${this.token}` }
        });
        this.currentStatus = response.data;
      } catch (error) {
        console.error('Error checking Shopify status:', error);
      }
    },
    
    checkForOAuthErrors() {
      const urlParams = new URLSearchParams(window.location.search);
      const shopifyError = urlParams.get('shopify');
      const reason = urlParams.get('reason');
      
      if (shopifyError === 'error') {
        this.oauthError = {
          type: 'oauth_error',
          reason: reason || 'unknown',
          message: this.getErrorMessage(reason)
        };
      }
    },
    
    getErrorMessage(reason) {
      switch (reason) {
        case 'state_mismatch':
          return 'The OAuth process was interrupted. This can happen if you navigated away during the process or if multiple requests were made.';
        case 'cookie_not_found':
          return 'The OAuth cookie was not found. This can happen due to browser security settings or cross-domain issues. Please try again or check your browser settings.';
        case 'no_business_id':
          return 'No business ID was provided. Please make sure you are logged in and have selected a business.';
        case 'business_not_found':
          return 'The specified business was not found in the database. Please contact support.';
        case 'database_error':
          return 'A database error occurred while saving the Shopify connection. Please try again.';
        case 'no_shop':
          return 'No Shopify store was specified for the connection.';
        default:
          return 'An error occurred during the Shopify connection process.';
      }
    },
    
    async installApp() {
      this.isConnecting = true;
      this.error = '';
      this.oauthError = null;

      // Only use shop parameter from URL
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      if (!shop || shop.trim() === '' || shop === 'undefined' || shop === 'null') {
        this.error = 'No valid Shopify URL found. Please install the StarkShopper app on your store first and open the app up from your Shopify store.';
        this.isConnecting = false;
        return;
      }
      const oauthUrl = `${process.env.VUE_APP_API_URL}/api/shopify/auth/start?shop=${encodeURIComponent(shop)}`;
      console.log('OAuth start URL:', oauthUrl, 'shop:', shop);
      window.location.href = oauthUrl;
    },
    
    async retryOAuth() {
      // Always use user.business_id from localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      const businessId = user?.business_id;
      if (!businessId || typeof businessId !== 'string' || businessId.trim() === '' || businessId === 'undefined' || businessId === 'null') {
        this.error = 'No valid business ID found for your user. Please log out and log back in, or contact support.';
        return;
      }

      this.isConnecting = true;
      this.oauthError = null;

      try {
        // Retry the direct OAuth flow
        const retryUrl = `${process.env.VUE_APP_API_URL}/api/shopify/auth/direct?business_id=${businessId}`;
        window.location.href = retryUrl;
      } catch (error) {
        this.error = 'Failed to retry connection. Please try again.';
        this.isConnecting = false;
      }
    },
    

    
    async disconnectStore() {
      this.error = '';
      this.successMessage = '';
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const businessId = user?.business_id;
        if (!businessId) {
          this.error = 'No business ID found for your user. Please contact support.';
          return;
        }
        // Preferred DELETE route
        await axios
          .delete(`${process.env.VUE_APP_API_URL}/api/shopify/disconnect/${businessId}`, {
            headers: { Authorization: `Bearer ${this.token}` }
          })
          .catch(async () => {
            // Try generic endpoint without param (auto-resolve)
            await axios.delete(`${process.env.VUE_APP_API_URL}/api/shopify/disconnect`, {
              headers: { Authorization: `Bearer ${this.token}` },
              data: { business_id: businessId }
            });
          });
        this.successMessage = 'Shopify store disconnected successfully.';
        await this.checkCurrentStatus();
        setTimeout(()=>{ this.successMessage=''; },5000);
      } catch (err) {
        console.error('Primary disconnect failed:', err?.response || err);
        // Fallback to legacy POST if DELETE fails (older backend versions)
        try {
          const user = JSON.parse(localStorage.getItem('user'));
          const businessId = user?.business_id;
            await axios.post(`${process.env.VUE_APP_API_URL}/api/shopify/disconnect`, { business_id: businessId }, {
              headers: { 'Authorization': `Bearer ${this.token}` }
            });
            this.successMessage = 'Shopify store disconnected (fallback).';
            await this.checkCurrentStatus();
            setTimeout(()=>{ this.successMessage=''; },5000);
        } catch (fallbackErr) {
          console.error('Fallback disconnect failed:', fallbackErr?.response || fallbackErr);
          this.error = 'Failed to disconnect store. Please try again or contact support.';
        }
      }
    }
  },
  mounted() {
    // Enforce immediate OAuth before any UI is shown
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get('shop');
    const token = localStorage.getItem('token');
    // If not authenticated (no token), always redirect to OAuth if shop param is present
    if (!token) {
      if (shop && shop !== '' && shop !== 'undefined' && shop !== 'null') {
        const oauthUrl = `${process.env.VUE_APP_API_URL}/api/shopify/auth/start?shop=${encodeURIComponent(shop)}`;
        window.location.replace(oauthUrl);
      } else {
        // If no shop param, show error (should never happen from Shopify install)
        this.error = 'Missing shop parameter. Please install the app from your Shopify store.';
      }
      return;
    }

    // If authenticated, proceed as normal
    this.checkedAuth = true;
    this.checkCurrentStatus();
    this.checkForOAuthErrors();
    // Set shopify_business_id cookie if user is logged in
    const user = localStorage.getItem('user');
    let businessId = null;
    try {
      businessId = JSON.parse(user)?.business_id;
    } catch {
      // Ignore JSON parse errors, businessId will remain null
    }
    if (businessId !== undefined && businessId !== null && businessId !== '' && businessId !== 'undefined' && businessId !== 'null') {
      businessId = String(businessId).trim();
      if (businessId !== '') {
        document.cookie = `shopify_business_id=${businessId}; path=/; SameSite=None; Secure; max-age=3600`;
      }
    }
    if (urlParams.get('shopify') === 'connected') {
      this.successMessage = 'Your Shopify store is now connected!';
      setTimeout(() => { this.successMessage = ''; }, 5000);
    }
  }
};
</script>

<style scoped>
.dashboard-content {
  margin-left: 300px;
  flex-grow: 1;
  padding: 20px;
  overflow-y: auto;
  height: 100vh;
  box-sizing: border-box;
}

.connect-container {
  max-width: 800px;
  margin: 0 auto;
}

.prompt-sync-message {
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 8px;
  padding: 18px;
  margin-bottom: 25px;
  color: #1565c0;
  font-size: 16px;
  text-align: center;
}
.prompt-icon {
  font-size: 22px;
  margin-right: 8px;
}
.prompt-details {
  margin-top: 8px;
  font-size: 15px;
  color: #1976d2;
}



.connect-form {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 30px;
  margin-bottom: 30px;
}

.connect-form h3 {
  margin: 0 0 20px 0;
  color: #333;
}

.connect-info {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
}

.connect-info p {
  margin: 0 0 15px 0;
  color: #495057;
  font-size: 16px;
}

.connect-info ul {
  margin: 0;
  padding-left: 20px;
}

.connect-info li {
  margin-bottom: 8px;
  color: #495057;
  font-size: 14px;
}

.install-actions {
  display: flex;
  gap: 15px;
  margin: 30px 0;
  justify-content: center;
}

.connect-btn.primary {
  background: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  text-decoration: none;
  display: inline-block;
  width: 100%;
  text-align: center;
}

.connect-btn.primary:hover {
  background: #0056b3;
}



.connect-btn {
  width: 100%;
  padding: 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.connect-btn:hover:not(:disabled) {
  background: #0056b3;
}

.connect-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.error-message {
  margin-top: 15px;
  padding: 10px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
}

.current-status {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
}

.current-status h3 {
  margin: 0 0 15px 0;
  color: #333;
}

.status-connected {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #28a745;
  font-weight: 500;
}

.status-disconnected {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #dc3545;
  font-weight: 500;
}

.status-icon {
  font-size: 20px;
}

.disconnect-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-left: auto;
}

.disconnect-btn:hover {
  background: #c82333;
}

/* Media Queries */
@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 0;
    margin-top: 100px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    padding: 15px;
  }
  
  .connect-container {
    max-width: 100%;
  }
  
  .domain-input-container {
    flex-direction: column;
  }
  
  .form-control {
    border-radius: 4px;
    margin-bottom: 10px;
  }
}

.oauth-error-message {
  margin-top: 15px;
  padding: 15px;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  color: #856404;
}

.oauth-error-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.oauth-error-header h4 {
  margin: 0;
  color: #856404;
}

.error-icon {
  font-size: 18px;
}

.oauth-error-message p {
  margin: 0 0 15px 0;
  line-height: 1.4;
}

.oauth-error-actions {
  display: flex;
  gap: 10px;
}

.retry-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.retry-btn:hover:not(:disabled) {
  background: #0056b3;
}

.retry-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.dismiss-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.dismiss-btn:hover {
  background: #545b62;
}

.success-message {
  margin-top: 15px;
  padding: 12px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  color: #155724;
  font-weight: 500;
  text-align: center;
}
</style> 